import { useState } from "react";
import { Button } from "@/components/wensity/button";
import { Input } from "@/components/wensity/input";
import { Textarea } from "@/components/wensity/text-area";
import { Card, CardContent, CardHeader } from "@/components/wensity/card";
import {
  Dialog,
  DialogConfirmation,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogScrollBody,
  DialogTitle,
} from "@/components/wensity/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/wensity/table";
import type { LinkItem, LinkSection } from "@/data/site";

function emptyItem(): LinkItem {
  return { name: "", url: "", description: "", category: "" };
}

interface DialogTarget {
  sectionIndex: number;
  itemIndex: number | null;
}

interface LinkSectionsEditorProps {
  sections: LinkSection[];
  onChange: (sections: LinkSection[]) => void;
}

export function LinkSectionsEditor({ sections, onChange }: LinkSectionsEditorProps) {
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null);
  const [draft, setDraft] = useState<LinkItem>(emptyItem());
  const [deleteTarget, setDeleteTarget] = useState<{ sectionIndex: number; itemIndex: number } | null>(null);

  function addSection() {
    onChange([...sections, { title: "New section", items: [] }]);
  }

  function updateSectionTitle(index: number, title: string) {
    onChange(sections.map((section, i) => (i === index ? { ...section, title } : section)));
  }

  function deleteSection(index: number) {
    onChange(sections.filter((_, i) => i !== index));
  }

  function openNewItem(sectionIndex: number) {
    setDraft(emptyItem());
    setDialogTarget({ sectionIndex, itemIndex: null });
  }

  function openEditItem(sectionIndex: number, itemIndex: number) {
    setDraft(sections[sectionIndex].items[itemIndex]);
    setDialogTarget({ sectionIndex, itemIndex });
  }

  function handleSaveItem() {
    if (!dialogTarget) return;
    const { sectionIndex, itemIndex } = dialogTarget;
    onChange(
      sections.map((section, i) => {
        if (i !== sectionIndex) return section;
        const items =
          itemIndex === null
            ? [...section.items, draft]
            : section.items.map((item, j) => (j === itemIndex ? draft : item));
        return { ...section, items };
      }),
    );
    setDialogTarget(null);
  }

  function handleDeleteItem(sectionIndex: number, itemIndex: number) {
    onChange(
      sections.map((section, i) =>
        i === sectionIndex ? { ...section, items: section.items.filter((_, j) => j !== itemIndex) } : section,
      ),
    );
    setDeleteTarget(null);
  }

  const isValid = draft.name.trim().length > 0 && draft.url.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="brand" onClick={addSection}>
          Add section
        </Button>
      </div>

      {sections.map((section, sectionIndex) => (
        <Card key={sectionIndex} className="border-[var(--color-border)] bg-[rgba(20,24,22,0.72)]">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <Input
              value={section.title}
              onChange={(event) => updateSectionTitle(sectionIndex, event.target.value)}
              containerClassName="max-w-xs"
              aria-label="Section title"
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openNewItem(sectionIndex)}>
                Add item
              </Button>
              <Button variant="destructive" size="sm" onClick={() => deleteSection(sectionIndex)}>
                Delete section
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {section.items.map((item, itemIndex) => (
                  <TableRow key={`${item.name}-${itemIndex}`}>
                    <TableCell>
                      <div className="font-medium text-[var(--color-foreground)]">{item.name}</div>
                      <div className="text-xs text-[var(--color-muted)]">{item.description}</div>
                    </TableCell>
                    <TableCell className="max-w-[16rem] truncate font-mono text-xs">{item.url}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditItem(sectionIndex, itemIndex)}>
                          Edit
                        </Button>
                        <DialogConfirmation
                          open={deleteTarget?.sectionIndex === sectionIndex && deleteTarget?.itemIndex === itemIndex}
                          onOpenChange={(open) => setDeleteTarget(open ? { sectionIndex, itemIndex } : null)}
                          trigger={
                            <Button variant="destructive" size="sm">
                              Delete
                            </Button>
                          }
                          title={`Delete "${item.name}"?`}
                          confirmLabel="Delete"
                          destructive
                          onConfirm={() => handleDeleteItem(sectionIndex, itemIndex)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {section.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-[var(--color-muted)]">
                      No items yet.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}

      <Dialog open={dialogTarget !== null} onOpenChange={(open) => !open && setDialogTarget(null)}>
        <DialogContent variant="form">
          <DialogHeader>
            <DialogTitle>{dialogTarget?.itemIndex === null ? "New item" : "Edit item"}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody className="space-y-4">
            <Input
              label="Name"
              value={draft.name}
              onChange={(event) => setDraft((d) => ({ ...d, name: event.target.value }))}
            />
            <Input
              label="URL"
              value={draft.url}
              onChange={(event) => setDraft((d) => ({ ...d, url: event.target.value }))}
            />
            <Textarea
              label="Description"
              value={draft.description}
              onChange={(event) => setDraft((d) => ({ ...d, description: event.target.value }))}
            />
            <Input
              label="Category"
              hint="Optional"
              value={draft.category ?? ""}
              onChange={(event) => setDraft((d) => ({ ...d, category: event.target.value }))}
            />
          </DialogScrollBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogTarget(null)}>
              Cancel
            </Button>
            <Button variant="brand" onClick={handleSaveItem} disabled={!isValid}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
